import { Box, Fade, Modal, Stack, Typography, Divider, Button } from '@mui/material';
import { PropsWithChildren, ReactElement } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import dayjs from 'dayjs';
import i18n from '../../../../../libs/language';

type SharedIDData = {
  open: boolean;
  name: {
    organization: string;
    user: string;
  };
  permissions: {
    current: {
      read: boolean;
      write: boolean;
      full: boolean;
    };
    previous: {
      read: boolean;
      write: boolean;
      full: boolean;
    };
  };
  extra: {
    event: string;
    siloID: string;
    date: string;
  };
  onClose: () => void;
};

// Función para determinar el permiso más elevado
function getHighestPermission(permissions: { read: boolean; write: boolean; full: boolean }): {
  label: string;
  color: string;
  icon: ReactElement;
  bgColor: string;
} | null {
  if (permissions.full) {
    return {
      label: i18n.t('app.silobag_share.access_complete'),
      color: '#6BAA00',
      icon: <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />,
      bgColor: 'rgba(107, 170, 0, 0.08)',
    };
  }
  if (permissions.write) {
    return {
      label: i18n.t('app.edit'),
      color: '#22A7F0',
      icon: <EditIcon sx={{ fontSize: 20 }} />,
      bgColor: 'rgba(34, 167, 240, 0.08)',
    };
  }
  if (permissions.read) {
    return {
      label: i18n.t('app.silobag_share.view'),
      color: '#7A7A7A',
      icon: <VisibilityIcon sx={{ fontSize: 20 }} />,
      bgColor: 'rgba(122, 122, 122, 0.08)',
    };
  }
  return null;
}

// Función para verificar si tiene algún permiso
function hasAnyPermission(permissions: { read: boolean; write: boolean; full: boolean }): boolean {
  return permissions.read || permissions.write || permissions.full;
}

export function SharedIDModal(props: SharedIDData) {
  const { open, onClose } = props;
  if (!open) return null;

  const { name, permissions, extra } = props;

  const previousPermission = getHighestPermission(permissions.previous);
  const currentPermission = getHighestPermission(permissions.current);

  const hadPermissionBefore = hasAnyPermission(permissions.previous);
  const hasPermissionNow = hasAnyPermission(permissions.current);

  return (
    <MyModal open={open} onClose={onClose}>
      <Stack spacing={2.5}>
        {/* Header */}
        <Box>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              color: '#49454F',
              fontSize: '1.25rem',
            }}
          >
            {i18n.t('app.detail')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#7A7A7A',
              fontSize: '0.875rem',
              mt: 0.5,
            }}
          >
            {i18n.t('app.silobag_share.access_information')}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: '#E0E0E0' }} />

        {/* Información principal */}
        <Stack spacing={2}>
          {/* Organización */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: '#7A7A7A',
                fontSize: '0.66rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {i18n.t('app.organization')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#49454F',
                fontWeight: 500,
                fontSize: '1rem',
                mt: 0.5,
              }}
            >
              {name.organization}
            </Typography>
          </Box>

          {/* Usuario */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: '#7A7A7A',
                fontSize: '0.66rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {i18n.t('app.user')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#49454F',
                fontWeight: 500,
                fontSize: '1rem',
                mt: 0.5,
              }}
            >
              {name.user}
            </Typography>
          </Box>

          {/* Evolución de permisos */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: '#7A7A7A',
                fontSize: '0.66rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1,
                display: 'block',
              }}
            >
              {hadPermissionBefore && hasPermissionNow
                ? i18n.t('app.silobag_share.permission_change')
                : hasPermissionNow
                ? i18n.t('app.silobag_share.permission_granted')
                : i18n.t('app.silobag_share.removed_permission')}
            </Typography>

            {/* Caso 1: Tiene permiso anterior y actual - Mostrar evolución */}
            {hadPermissionBefore && hasPermissionNow && previousPermission && currentPermission && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                {/* Permiso anterior */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: previousPermission.bgColor,
                    border: `1.5px dashed ${previousPermission.color}`,
                    opacity: 0.7,
                  }}
                >
                  <Box sx={{ color: previousPermission.color, display: 'flex', alignItems: 'center' }}>
                    {previousPermission.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: previousPermission.color,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                    }}
                  >
                    {previousPermission.label}
                  </Typography>
                </Box>

                {/* Flecha */}
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>→</Typography>

                {/* Permiso actual */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: currentPermission.bgColor,
                    border: `2px solid ${currentPermission.color}`,
                    flex: 1,
                  }}
                >
                  <Box sx={{ color: currentPermission.color, display: 'flex', alignItems: 'center' }}>
                    {currentPermission.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: currentPermission.color,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {currentPermission.label}
                  </Typography>
                </Box>
              </Stack>
            )}

            {/* Caso 2: No tenía permiso antes, ahora sí - Permiso otorgado */}
            {!hadPermissionBefore && hasPermissionNow && currentPermission && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                {/* Sin acceso anterior */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: 'rgba(122, 122, 122, 0.05)',
                    border: '1.5px dashed #BDBDBD',
                    opacity: 0.6,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#9E9E9E',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      fontStyle: 'italic',
                    }}
                  >
                    {i18n.t('app.silobag_share.without_access')}
                  </Typography>
                </Box>

                {/* Flecha */}
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>→</Typography>

                {/* Permiso actual */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: currentPermission.bgColor,
                    border: `2px solid ${currentPermission.color}`,
                    flex: 1,
                  }}
                >
                  <Box sx={{ color: currentPermission.color, display: 'flex', alignItems: 'center' }}>
                    {currentPermission.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: currentPermission.color,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {currentPermission.label}
                  </Typography>
                </Box>
              </Stack>
            )}

            {/* Caso 3: Tenía permiso antes, ahora no - Permiso removido */}
            {hadPermissionBefore && !hasPermissionNow && previousPermission && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                {/* Permiso anterior */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: previousPermission.bgColor,
                    border: `1.5px dashed ${previousPermission.color}`,
                    opacity: 0.7,
                  }}
                >
                  <Box sx={{ color: previousPermission.color, display: 'flex', alignItems: 'center' }}>
                    {previousPermission.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: previousPermission.color,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                    }}
                  >
                    {previousPermission.label}
                  </Typography>
                </Box>

                {/* Flecha */}
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>→</Typography>

                {/* Sin acceso */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: 'rgba(237, 9, 9, 0.08)',
                    border: '2px solid #ED0909',
                    flex: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CancelIcon sx={{ fontSize: 20, color: '#ED0909' }} />
                    <Typography
                      sx={{
                        color: '#ED0909',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      {i18n.t('app.silobag_share.removed')}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>

        <Divider sx={{ borderColor: '#E0E0E0' }} />

        {/* Información adicional */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#7A7A7A',
              fontSize: '0.66rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1.5,
              display: 'block',
            }}
          >
            {i18n.t('app.silobag_share.aditional_information')}
          </Typography>

          <Box
            sx={{
              backgroundColor: '#F3F2F7',
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Stack spacing={1.5}>
              {/* Tipo de evento */}
              <Stack direction="row" spacing={4} justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7A7A7A',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                >
                  {i18n.t('app.event')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#49454F',
                    fontWeight: 500,
                    fontSize: '0.85rem',
                  }}
                >
                  {extra.event}
                </Typography>
              </Stack>

              {/* ID de silobolsa */}
              <Stack direction="row" spacing={4} justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7A7A7A',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                >
                  {i18n.t('app.silobag_id')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#49454F',
                    fontWeight: 500,
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                  }}
                >
                  {extra.siloID}
                </Typography>
              </Stack>

              {/* Fecha */}
              <Stack direction="row" spacing={4} justifyContent="space-between" alignItems="center">
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7A7A7A',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                  }}
                >
                  {i18n.t('app.silobag_share.modification_date')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#49454F',
                    fontWeight: 500,
                    fontSize: '0.85rem',
                  }}
                >
                  {dayjs(extra.date).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>
        <Stack width="100%" direction="row" justifyContent="flex-end" alignItems="center">
          <Button
            variant="outlined"
            style={{
              width: 115,
              borderRadius: 100,
              textTransform: 'none',
              border: 'none',
              color: 'black',
            }}
            onClick={onClose}
          >
            {i18n.t('app.close')}
          </Button>
        </Stack>
      </Stack>
    </MyModal>
  );
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
};
function MyModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '85%',
    overflowX: 'hidden',
    maxHeight: '95vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style} component={'div'}>
          {children}
        </Box>
      </Fade>
    </Modal>
  );
}
